import { keyframes, ThemeProvider } from '@emotion/react';
import styled from '@emotion/styled';
import type { FocusEvent, FunctionComponent } from 'react';
import { useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import { useApplicationContext } from '~/shared/context/use-application-context';
import { theme } from '~/ui/theme/cobrainer-theme';

import { useGlobalPermissions } from '../../query-hooks/global-permissions';
import { CustomThemeProvider } from '../../theming';
import { VisibilityIndicator } from '../VisibilityIndicator';
import { GlobalSearchBarV2 } from './GlobalSearchBarV2';
import { NAV_WIDTH_TRANSITION_DURATION_MS, useNavigationMenu } from './NavigationMenuContext';
import { UserMenuV2 } from './UserMenuV2';
import { useSideDrawerLinks } from './use-side-drawer-links';

const fadeIn = keyframes({
    from: {
        opacity: 0,
    },
    to: {
        opacity: 1,
    },
});

export const COLLAPSED_NAV_WIDTH_PX = 48;
const EXPANDED_NAV_WIDTH_PX = 220;

const DrawerContainer = styled.div<{ readonly isExpanded: boolean }>(({ isExpanded, theme }) => ({
    animation: `${fadeIn} 500ms ease-in`,
    backgroundColor: theme.colors.gray[200],
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 60px)',
    justifyContent: 'space-between',
    padding: `${theme.spacing.m} ${theme.spacing.s}`,
    paddingTop: theme.spacing.s,
    position: 'sticky',
    top: '54px',
    transition: `width ${NAV_WIDTH_TRANSITION_DURATION_MS}ms cubic-bezier(0.16, 1, 0.3, 1)`,
    width: isExpanded ? `${EXPANDED_NAV_WIDTH_PX}px` : `${COLLAPSED_NAV_WIDTH_PX}px`,
}));

export const DrawerHeader = styled.div(({ theme }) => ({
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing['4xl'],
    paddingLeft: 0,
}));

const DrawerContent = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xl,
});

const MenuContainer = styled.div(({ theme }) => ({
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    gap: theme.spacing.m,
    justifyContent: 'space-between',
}));

const RightSection = styled.div(({ theme }) => ({
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing.s,
}));

export const SideDrawerNavigationV2: FunctionComponent = () => {
    const { isExpanded, setHovered } = useNavigationMenu();
    const { translate } = useContext(ApplicationContext);
    const { bottomItem, links } = useSideDrawerLinks();

    const handleBlur = (event: FocusEvent<HTMLDivElement>): void => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
            setHovered(false);
        }
    };

    return (
        <ThemeProvider theme={theme}>
            <DrawerContainer
                aria-label={translate('side-navigation.aria-label')}
                isExpanded={isExpanded}
                onBlur={handleBlur}
                onFocus={() => setHovered(true)}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
            >
                <DrawerContent>{links}</DrawerContent>
                {bottomItem}
            </DrawerContainer>
        </ThemeProvider>
    );
};

const CenteredWrapper = styled.div({
    '@media (min-width: 1600px)': {
        left: '50%',
        minWidth: '500px',
        position: 'absolute',
        transform: 'translateX(-50%)',
    },
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
});

export const NavigationMenuV2: FunctionComponent = () => {
    const { data: globalPermissions, isSuccess } = useGlobalPermissions();
    const { applicationState } = useApplicationContext();
    const isNewUserSettingsEnabled = applicationState.featureFlagService.isFeatureFlagEnabled('new-user-settings');

    return (
        <ThemeProvider theme={theme}>
            <MenuContainer>
                <div />
                <CenteredWrapper>
                    {isSuccess && globalPermissions.canUserSearch && <GlobalSearchBarV2 />}
                </CenteredWrapper>
                <RightSection>
                    {!isNewUserSettingsEnabled && (
                        <CustomThemeProvider>
                            <VisibilityIndicator />
                        </CustomThemeProvider>
                    )}
                    <UserMenuV2 />
                </RightSection>
            </MenuContainer>
        </ThemeProvider>
    );
};
